import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Box } from '../../1-primative';
import { DateField } from './dateField';
import { Popover, Button, TextField } from '..';

interface DateRangeFieldProps {
  label: string;
  value: [string | null, string | null];
  onChange: (range: [string | null, string | null]) => void;
  fullWidth?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  testId?: string;
  required?: boolean;
}

export const DateRangeField = ({
  label,
  value,
  onChange,
  fullWidth,
  disabled,
  minDate,
  maxDate,
  testId,
  required = false,
}: DateRangeFieldProps) => {
  const { t } = useTranslation('components');
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [startDate, endDate] = value;
  const [tempStartDate, setTempStartDate] = useState<string | null>(startDate);
  const [tempEndDate, setTempEndDate] = useState<string | null>(endDate);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onChange([tempStartDate, tempEndDate]);
    handleClose();
  };

  const handleClear = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    onChange([null, null]);
    handleClose();
  };

  const displayValue = startDate && endDate
    ? `${dayjs(startDate).format('MMM D, YYYY')} - ${dayjs(endDate).format('MMM D, YYYY')}`
    : '';

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          width: fullWidth ? '100%' : 'auto',
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
        data-testid={testId}
      >
        <TextField
          label={label}
          value={displayValue}
          fullWidth={fullWidth}
          disabled={disabled}
          inputProps={{ readOnly: true }}
        />
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box p={2} display='flex' flexDirection='column' gap={2} minWidth={300}>
          <Box display='flex' flexDirection='column' gap={1}>
            <DateField
              label={t('dateRangeField.from')}
              value={tempStartDate}
              onChange={setTempStartDate}
              fullWidth
              minDate={minDate}
              maxDate={tempEndDate ? new Date(tempEndDate) : maxDate}
              required={required}
            />
            <DateField
              label={t('dateRangeField.to')}
              value={tempEndDate}
              onChange={setTempEndDate}
              fullWidth
              minDate={tempStartDate ? new Date(tempStartDate) : minDate}
              maxDate={maxDate}
              required={required}
            />
          </Box>
          <Box display='flex' justifyContent='flex-end' gap={1}>
            <Button
              variant='outlined'
              label={t('dateRangeField.clear')}
              onClick={handleClear}
            />
            <Button
              variant='filled'
              label={t('dateRangeField.apply')}
              onClick={handleApply}
            />
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangeField;
